// contexts/SuggestionContext.tsx
/**
 * SuggestionContext - Centralizes optimizer suggestion state and handlers
 * for the planner subtree (CoursePlanner, SemesterGrid, CourseCard).
 *
 * Usage:
 *   <SuggestionProvider value={{ onAcceptSuggestion, ... }}>
 *     <CoursePlanner ... />
 *   </SuggestionProvider>
 *
 *   const { onAcceptSuggestion, ... } = useSuggestions();
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { UseOptimizerActionsReturn } from '../hooks/useOptimizerActions';
import type { OptimizationStatus } from '../types';
import type { OptimizerMode } from '../types/optimizerModes';

// =============================================================================
// TYPES
// =============================================================================

// Mode registry lives in types/optimizerModes so the service layer can use it
// without importing from a React context (this module already imports from
// useOptimizerActions, so the reverse would be a cycle). Re-exported here for
// existing consumers.
export {
  OPTIMIZER_MODES,
  CHATBOT_ROUTED_MODES,
  DEFAULT_OPTIMIZER_MODE,
  OPTIMIZER_LABELS,
  OPTIMIZER_ROTATION,
} from '../types/optimizerModes';
export type { OptimizerMode } from '../types/optimizerModes';

export interface SuggestionContextValue {
  onAcceptSuggestion: UseOptimizerActionsReturn['acceptSuggestion'];
  onRejectSuggestion: UseOptimizerActionsReturn['rejectSuggestion'];
  onReassignGenEd: UseOptimizerActionsReturn['reassignGenEdArea'];
  acceptingSuggestion: UseOptimizerActionsReturn['acceptingCourse'];
  rejectingSuggestion: UseOptimizerActionsReturn['rejectingCourse'];
  isGenerating: UseOptimizerActionsReturn['isGenerating'];
  optimizationStatus: OptimizationStatus | null | undefined;
  optimizerMode: OptimizerMode;
  onGenerateSuggestions: () => void;
}

interface SuggestionProviderProps {
  value: SuggestionContextValue;
  children: ReactNode;
}

// =============================================================================
// CONTEXT
// =============================================================================

const SuggestionContext = createContext<SuggestionContextValue | null>(null);
SuggestionContext.displayName = 'SuggestionContext';

export const useSuggestions = (): SuggestionContextValue => {
  const context = useContext(SuggestionContext);
  if (!context) {
    throw new Error('useSuggestions must be used within a SuggestionProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

export const SuggestionProvider = ({ value, children }: SuggestionProviderProps) => {
  return (
    <SuggestionContext.Provider value={value}>
      {children}
    </SuggestionContext.Provider>
  );
};

export default SuggestionContext;
